/**
 * HTML Entity Decoder for Frontend
 * 
 * Decodes HTML entities from API responses that were encoded for JSON safety.
 * 
 * Industry Standard: Browser-native decoding using textarea element
 * Used by: jQuery, Angular, Vue, React ecosystem
 * 
 * Common use cases:
 * - API responses with encoded URLs
 * - Cron expressions with encoded slashes (every 15 minutes)
 * - Timezones with encoded slashes (Asia/Kolkata format)
 * - User-generated content with special characters
 * 
 * Why this approach?
 * - Browser-native (no external dependencies)
 * - Handles ALL HTML entities automatically
 * - Fast and reliable
 * - Industry-proven pattern
 */

/**
 * Decode HTML entities from a string
 * 
 * @param {string | null | undefined} html - String with HTML entities
 * @returns {string} Decoded string
 * 
 * @example
 * decodeHtmlEntities('Asia&#x2F;Kolkata')
 * // Returns: 'Asia/Kolkata'
 * 
 * @example
 * decodeHtmlEntities('Hello &amp; welcome')
 * // Returns: 'Hello & welcome'
 */
export function decodeHtmlEntities(html: string | null | undefined): string {
  if (!html || typeof html !== 'string') {
    return html || '';
  }
  
  // Quick check: if no entities, return as-is (performance optimization)
  if (!html.includes('&') && !html.includes('#')) {
    return html;
  }
  
  // ✅ INDUSTRY STANDARD: Use browser's native HTML parser
  // Creates a temporary textarea element and uses innerHTML to decode
  // This handles ALL HTML entities automatically (named and numeric)
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}

/**
 * Decode multiple fields in an object
 * Useful for processing API responses with multiple encoded fields
 * 
 * @param {T} obj - Object with potentially encoded strings
 * @param {string[]} fields - Field names to decode
 * @returns {T} Object with decoded fields
 * 
 * @example
 * decodeFields(schedule, ['timezone', 'cronSchedule'])
 * // Returns: { ...schedule, timezone: 'Asia/Kolkata', cronSchedule: 'star/15 * * * *' }
 * 
 * @example
 * decodeFields(webhook, ['url', 'name'])
 * // Returns: { ...webhook, url: 'https://example.com/path', name: 'Test & Prod' }
 */
export function decodeFields<T extends Record<string, any>>(
  obj: T,
  fields: (keyof T)[]
): T {
  const decoded = { ...obj };
  
  fields.forEach(field => {
    if (typeof decoded[field] === 'string') {
      decoded[field] = decodeHtmlEntities(decoded[field] as string) as any;
    }
  });
  
  return decoded;
}

/**
 * Decode all string fields in an object recursively
 * Useful when you don't know which fields might be encoded
 * 
 * @param {T} obj - Object with potentially encoded strings
 * @returns {T} Object with all string fields decoded
 * 
 * @example
 * decodeAllFields({ name: 'Test &amp; Demo', url: 'http:&#x2F;&#x2F;example.com' })
 * // Returns: { name: 'Test & Demo', url: 'http://example.com' }
 */
export function decodeAllFields<T extends Record<string, any>>(obj: T): T {
  const decoded: any = { ...obj };
  
  Object.keys(decoded).forEach((key: string) => {
    const value = decoded[key];
    
    if (typeof value === 'string') {
      decoded[key] = decodeHtmlEntities(value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively decode nested objects
      decoded[key] = decodeAllFields(value);
    } else if (Array.isArray(value)) {
      // Decode string arrays
      decoded[key] = value.map(item =>
        typeof item === 'string' ? decodeHtmlEntities(item) : item
      );
    }
  });
  
  return decoded as T;
}

